import { CheckCircleIcon } from "@chakra-ui/icons"
import type { ComponentWithAs, IconProps } from "@chakra-ui/react"
import {
  Button,
  Center,
  CircularProgress,
  Divider,
  Icon,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react"
import type { ReactNode } from "react"
import { type FC, useEffect, useMemo, useState } from "react"
import Link from "@/common/components/Link"
import { EXTERNAL_ROUTES } from "@/common/constants/routes"
import { pluralize } from "@/common/helpers/string"
import { useModals } from "@/common/hooks/useModals"
import {
  type FixedSafeMultisigTransactionResponse,
  useSafeTransactionV2,
} from "@/safe/hooks/useSafeTransactionV2"
import GenericModal from "@/ui/components/GenericModal"
import Check from "@/ui/components/icons/Check"
import { ArrowUpRight } from "@/ui/components/icons/font-awesome/ArrowUpRight"
import { useMe } from "@/user/providers/MeProvider"
import { isSameAddress } from "@/web3/helpers/address"
import { getChainByReference } from "@/web3/helpers/findChain"
import { useMultichainAccount } from "@/web3/hooks/useMultichainAccount"
import {
  TransactionState,
  type TransactionStatus,
} from "@/web3/types/transaction"

const PendingMessage = "Confirm transaction"

type TxStep = {
  state: TransactionState
  message: string
  icon: ComponentWithAs<"svg", IconProps>
}

type Props = {
  status: TransactionStatus<any>
  chainReference: number
  body?: ReactNode
  footer?: any //
  txSteps: TxStep[]
  txHash?: string
  safeTxHash?: string
}
const Web3StatusModal: FC<Props> = ({
  chainReference,
  status,
  footer,
  body,
  txSteps,
  txHash,
  safeTxHash,
}) => {
  const { web3StatusModal } = useModals()
  const me = useMe()
  const { multichainAccount: walletAccount } = useMultichainAccount()
  const canUseSafeTx =
    me?.type === "SAFE" && !isSameAddress(me.address, walletAccount.address)
  const state = status.state

  const [safeTx, setSafeTx] = useState<
    FixedSafeMultisigTransactionResponse | undefined
  >(undefined)
  const [pendingTransactions, setPendingTransactions] = useState<
    FixedSafeMultisigTransactionResponse[] | undefined
  >(undefined)

  const { getSafeTransaction, getPendingTransactions } = useSafeTransactionV2(
    {
      safeId: me?.id as string,
      accountAddress: me?.address,
    },
    { enabled: me?.type === "SAFE" },
  )
  // biome-ignore lint: biome-migration
  const { titleLabel, icon, localState } = useMemo(() => {
    if (txSteps.length === 0) {
      return { titleLabel: PendingMessage, localState: TransactionState.Begin }
    }

    if (txSteps[txSteps.length - 1].state === TransactionState.Success) {
      return {
        titleLabel: `${txSteps[txSteps.length - 1].message} Success`,
        icon: <Icon as={CheckCircleIcon} color="green.500" h="44px" w="44px" />,
        localState: TransactionState.Success,
      }
    }

    const lastNonSuccessTxStep = txSteps
      .slice()
      .reverse()
      .find((txStep) => txStep.state !== TransactionState.Success)

    return {
      titleLabel: lastNonSuccessTxStep?.message ?? PendingMessage,
      localState: lastNonSuccessTxStep?.state ?? TransactionState.Begin,
    }
  }, [state, txSteps])

  const handleClose = () => {
    web3StatusModal.onClose()
  }
  // biome-ignore lint: biome-migration
  useEffect(() => {
    if (me?.type === "SAFE" && safeTxHash) {
      getSafeTransaction(safeTxHash).then((safeTx) => {
        setSafeTx(safeTx)
      })

      getPendingTransactions().then((results) => {
        setPendingTransactions(results)
      })
    }
  }, [
    getSafeTransaction,
    getPendingTransactions,
    me?.address,
    me?.type,
    safeTxHash,
  ])

  const pendingSafeTx = pendingTransactions
    ? pendingTransactions.find((pendingTx) => {
        if (pendingTx.safeTxHash === safeTx?.safeTxHash) {
          return pendingTx
        }
      })
    : undefined

  const getTxStateIcon = (txStep: TxStep) => {
    if (
      txStep.state === TransactionState.Mining ||
      txStep.state === TransactionState.PendingSafe
    ) {
      return <CircularProgress isIndeterminate color="purple.500" size="25px" />
    }

    if (txStep.state === TransactionState.Begin) {
      return (
        <Icon
          as={txStep.icon}
          bgColor="purple.500"
          borderRadius="50%"
          color="white"
          height="25px"
          p="5px"
          width="25px"
        />
      )
    }

    if (txStep.state === TransactionState.Success) {
      return (
        <Icon
          as={txStep.icon}
          bgColor="gray.100"
          borderRadius="50%"
          color="gray.400"
          height="25px"
          p="5px"
          width="25px"
        />
      )
    }

    return (
      <Icon
        as={txStep.icon}
        bgColor="gray.100"
        borderRadius="50%"
        color="gray.500"
        height="25px"
        p="5px"
        width="25px"
      />
    )
  }

  const getTxStateMessage = (txStep: TxStep) => {
    if (txStep.state === TransactionState.Begin) {
      return (
        <Text color="gray.800" fontSize="md" fontWeight="500">
          {canUseSafeTx
            ? `${txStep.message}: sign in your wallet`
            : `${txStep.message} in wallet`}
        </Text>
      )
    }

    if (txStep.state === TransactionState.PendingSafe) {
      if (pendingSafeTx) {
        const confirmationsCount = pendingSafeTx.confirmations?.length ?? 0

        if (pendingSafeTx.confirmationsRequired > confirmationsCount) {
          const pendingConfirmationsCount =
            pendingSafeTx.confirmationsRequired - confirmationsCount

          return (
            <Text color="gray.800" fontSize="md" fontWeight="500">
              Continue in your Safe. Still {pendingConfirmationsCount}{" "}
              {pluralize(
                pendingConfirmationsCount,
                "confirmation",
                "confirmations",
              )}{" "}
              is required
            </Text>
          )
        }
      }

      return (
        <Text color="gray.800" fontSize="md" fontWeight="500">
          Continue in your Safe
        </Text>
      )
    }

    if (txStep.state === TransactionState.Mining) {
      return (
        <Text color="gray.800" fontSize="md" fontWeight="500">
          {txStep.message} pending...
        </Text>
      )
    }

    if (txStep.state === TransactionState.Success) {
      return (
        <Text color="gray.400" fontSize="md" fontWeight="500">
          {txStep.message}
        </Text>
      )
    }

    if (txStep.state === TransactionState.Fail) {
      return (
        <Text color="gray.400" fontSize="md" fontWeight="500">
          {txStep.message} failed or rejected
        </Text>
      )
    }

    return (
      <Text color="gray.800" fontSize="md" fontWeight="500">
        {txStep.message}
      </Text>
    )
  }

  const foundChain = canUseSafeTx
    ? getChainByReference(chainReference)
    : undefined

  return (
    <GenericModal
      height="auto"
      isOpen={web3StatusModal.isOpen}
      maxWidth="450px"
      top={{ base: "30%", md: "30%" }}
      onClose={handleClose}
    >
      <Stack align="center" maxHeight="500px" mt={12} spacing={4}>
        {icon ? <>{icon}</> : null}

        <Text fontSize="2xl" fontWeight="semibold">
          {titleLabel}
        </Text>

        {body ? <>{body}</> : null}

        {!txHash && txSteps && txSteps.length > 0 ? (
          <Stack spacing={0} width="100%">
            <Divider mb={4} mt={2} />
            {txSteps.map((txStep, idx) => (
              <>
                <Stack key={`txstep-${idx}`} align="center" direction="row">
                  <Center width="30px">
                    <>{getTxStateIcon(txStep)}</>
                  </Center>

                  <>{getTxStateMessage(txStep)}</>
                  <Spacer />
                  {txStep.state === TransactionState.Success ? (
                    <Icon
                      as={Check}
                      color="green.600"
                      height="20px"
                      width="20px"
                    />
                  ) : null}

                  {canUseSafeTx &&
                  foundChain &&
                  txStep.state === TransactionState.PendingSafe ? (
                    <Link
                      isExternal
                      href={EXTERNAL_ROUTES.safe.txsQueue(
                        foundChain.shortName,
                        me.address,
                      )}
                    >
                      <Button
                        color="purple.500"
                        fontSize="md"
                        rightIcon={
                          <Icon
                            as={ArrowUpRight}
                            fill="primary.500"
                            h="4"
                            w="4"
                          />
                        }
                        variant="link"
                      >
                        View on Safe
                      </Button>
                    </Link>
                  ) : null}
                </Stack>

                {idx < txSteps.length - 1 ? (
                  <Center height="10px" my={1} width="30px">
                    <Divider borderWidth="1.5px" orientation="vertical" />
                  </Center>
                ) : null}
              </>
            ))}
          </Stack>
        ) : null}

        {txHash ? (
          <Link
            isExternal
            href={EXTERNAL_ROUTES.etherscan.hash(chainReference, txHash)}
            mt={4}
          >
            <Button color="purple.500" fontSize="md" variant="link">
              View on block explorer
            </Button>
          </Link>
        ) : null}

        {canUseSafeTx ? (
          <Stack>
            <Divider mt={2} />
            <Text color="gray.800" fontSize="sm">
              Once the transaction is executed in the Safe, please close this
              popup
            </Text>
          </Stack>
        ) : null}

        {Boolean(footer?.[localState]) ? <>{footer?.[localState]}</> : null}
      </Stack>
    </GenericModal>
  )
}

export default Web3StatusModal
